import { getWorkspace } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";

import { navTree, routeFor } from "./navigation";
import type { NavAccess } from "./navigation";

/** A page the reader may open, taken from the sidebar tree itself. */
export interface SearchPage {
    id: string;
    label: string;
    hint: string;
    icon: string;
    route: string;
}

/** Arabic letters are written in more than one way; folding both forms to one
 *  lets a reader type the word as they say it and still find the page. */
const FOLD: Record<string, string> = {
    "أ": "ا", "إ": "ا", "آ": "ا", "ٱ": "ا",
    "ؤ": "و", "ئ": "ي", "ى": "ي", "ة": "ه",
};

/** The harakat, the Quranic marks, and the tatweel are not letters. */
const MARKS = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g;

export function foldText(value: string): string {
    return value
        .replace(MARKS, "")
        .replace(/[أإآٱؤئىة]/g, (letter) => FOLD[letter] ?? letter)
        .toLocaleLowerCase()
        .trim();
}

/** Every page the sidebar offers this reader, flattened in its own order.
 *
 *  The tree drops every application, group, model, and workspace the server
 *  denied, so this list can never be wider than the sidebar itself.  No access
 *  verdict means no pages: the tree must not be read before the server speaks.
 */
export function pageList(t: (key: string) => string, access: NavAccess | undefined,
                         isOwnerOrg?: boolean, mode: Workspace = getWorkspace()): SearchPage[] {
    if (!access) {
        return [];
    }
    const pages: SearchPage[] = [];
    for (const app of navTree(t, access, undefined, isOwnerOrg, mode)) {
        let heading = "";
        for (const child of app.children ?? []) {
            if (!child.selectable) {
                heading = child.label;
                continue;
            }
            const route = routeFor(child.id, mode);
            if (!route) {
                continue;
            }
            pages.push({
                id: child.id,
                label: child.label,
                hint: heading ? `${app.label} › ${heading}` : app.label,
                icon: child.icon || app.icon || "fa-file-text-o",
                route,
            });
        }
    }
    return pages;
}

/** Pages whose label, or the menu path above them, carries every word typed.
 *
 *  A label match always ranks above a path match, and the sidebar order breaks
 *  every tie, so the first page is the one the reader would have found first.
 */
export function matchPages(pages: SearchPage[], term: string, limit: number): SearchPage[] {
    const needle = foldText(term);
    if (!needle) {
        return [];
    }
    const words = needle.split(/\s+/).filter(Boolean);
    const ranked: Array<{ page: SearchPage; score: number; order: number }> = [];
    pages.forEach((page, order) => {
        const label = foldText(page.label);
        const path = `${label} ${foldText(page.hint)}`;
        if (!words.every((word) => path.includes(word))) {
            return;
        }
        let score = 4;
        if (label === needle) {
            score = 0;
        } else if (label.startsWith(needle)) {
            score = 1;
        } else if (label.includes(needle)) {
            score = 2;
        } else if (words.every((word) => label.includes(word))) {
            score = 3;
        }
        ranked.push({ page, score, order });
    });
    ranked.sort((a, b) => a.score - b.score || a.order - b.order);
    return ranked.slice(0, limit).map((entry) => entry.page);
}
